import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/** Viewport width below which normal theme drawers open as modal overlays. */
const MODAL_BREAKPOINT = 990;

/**
 * A drawer that opens from the right side.
 *
 * CART DRAWER:
 * The cart drawer always opens as a modal overlay on every screen size.
 *
 * OTHER DRAWERS:
 * On wide viewports (≥ 990px), other drawers keep the original
 * non-modal/sidebar behavior.
 *
 * On narrow viewports (< 990px), all drawers open as modal dialogs.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia(`(max-width: ${MODAL_BREAKPOINT - 1}px)`);

  /**
   * Cart drawer should ALWAYS be modal.
   * Other drawers are modal only below the normal breakpoint.
   *
   * @returns {boolean}
   */
  #shouldUseModal() {
    return this.id === 'cart-drawer' || this.#modalQuery.matches;
  }

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#modalQuery.addEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    const restoredOrder = Number(
      this.style.getPropertyValue('--drawer-stack-order') || 0
    );

    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    /*
     * Never restore the cart drawer after a fresh page load.
     * Clean the restored state completely so the page stays full width.
     */
    if (this.id === 'cart-drawer' && this.isOpen) {
      this.#resetRestoredCartDrawer();
      return;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Fully clears a cart drawer that was restored before this module loaded.
   */
  #resetRestoredCartDrawer() {
    const { panel } = this.refs;

    this.removeAttribute('open');
    this.style.removeProperty('--drawer-stack-order');

    panel.classList.remove(
      'theme-drawer__dialog--opening',
      'theme-drawer__dialog--opening-inline-start',
      'theme-drawer__dialog--closing'
    );

    if (panel.open) {
      panel.close();
    }

    unlockScroll(panel);
    removeTrapFocus();

    this.#removeEventListeners();

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    /*
     * Important:
     * Notify the rest of the theme that the drawer is closed.
     * This helps clear any persisted or squeezed layout state.
     */
    this.dispatchEvent(new DrawerCloseEvent());
  }

  /**
   * Restore path for non-cart drawers.
   */
  #onRestore() {
    const { panel } = this.refs;

    if (this.#shouldUseModal()) {
      lockScroll(panel);
    }

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    unlockScroll(this.refs.panel);

    this.#modalQuery.removeEventListener(
      'change',
      this.#onModalBreakpointChange
    );

    this.#removeEventListeners();

    removeTrapFocus();

    this.#deferredOpen = false;
  }

  /**
   * Closes the drawer when the user clicks the backdrop.
   *
   * @param {MouseEvent} event
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean}
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (
      this.refs.panel.querySelector('dialog[open]')
    );
  }

  /**
   * Switches normal drawers between modal and non-modal
   * when crossing the breakpoint.
   *
   * Cart drawer is always modal.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    if (this.id === 'cart-drawer') return;

    const { panel } = this.refs;

    const nestedDialog = this.#getOpenNestedDialog();

    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) &&
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    panel.close();

    removeTrapFocus();

    if (this.#shouldUseModal()) {
      lockScroll(panel);

      panel.showModal();
    } else {
      unlockScroll(panel);

      panel.show();

      trapFocus(panel);
    }

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(
        nestedDialog,
        nestedActiveElement
      );
    } else {
      this.#focusInitialElement();
    }

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  };

  /**
   * Closes the drawer when the user presses Escape.
   *
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    if (
      event.isComposing ||
      event.defaultPrevented
    ) {
      return;
    }

    if (!this.isOpen) return;

    if (!this.#isTopmost()) return;

    event.preventDefault();

    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  open({ enter } = {}) {
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }

    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    this.#previouslyFocused =
      /** @type {HTMLElement | null} */ (
        document.activeElement
      );

    /*
     * Cart drawer always uses showModal(),
     * including desktop.
     */
    if (this.#shouldUseModal()) {
      lockScroll(panel);

      panel.showModal();
    } else {
      panel.show();

      trapFocus(panel);
    }

    this.#focusInitialElement();

    onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      {
        subtree: false,
      }
    );

    this.setAttribute(
      'open',
      ''
    );

    this.dispatchEvent(
      new DrawerOpenEvent()
    );

    document.addEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.addEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  /**
   * Brings an already-open drawer to the front.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter]
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;

    this.style.setProperty(
      '--drawer-stack-order',
      String(ThemeDrawer.#stackOrder)
    );

    if (
      this.#shouldUseModal() &&
      panel.open
    ) {
      lockScroll(panel);

      panel.close();

      panel.showModal();
    }

    const openClass =
      enter === 'inline-start'
        ? 'theme-drawer__dialog--opening-inline-start'
        : 'theme-drawer__dialog--opening';

    panel.classList.add(openClass);

    onAnimationEnd(
      panel,
      () => panel.classList.remove(openClass),
      {
        subtree: false,
      }
    );

    this.dispatchEvent(
      new DrawerOpenEvent()
    );
  }

  /**
   * Removes drawer event listeners.
   */
  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener(
      'keydown',
      this.#onKeyDown
    );

    panel.removeEventListener(
      'click',
      this.#onBackdropClick
    );
  }

  /**
   * Moves focus to the close button.
   */
  #focusInitialElement() {
    const closeButton =
      /** @type {HTMLElement | null} */ (
        this.refs.panel.querySelector(
          '.theme-drawer__close-button'
        )
      );

    closeButton?.focus();
  }

  /**
   * Reopens nested modal above the drawer.
   *
   * @param {HTMLDialogElement} dialog
   * @param {HTMLElement | null} focusTarget
   */
  #bringNestedDialogToFront(
    dialog,
    focusTarget
  ) {
    dialog.close();

    dialog.showModal();

    if (
      focusTarget &&
      document.contains(focusTarget) &&
      dialog.contains(focusTarget)
    ) {
      focusTarget.focus();
    }
  }

  /**
   * Slides drawer out and closes dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;

    if (this.#isClosing) return;

    this.#isClosing = true;

    this.#removeEventListeners();

    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');

    this.dispatchEvent(
      new DrawerCloseEvent()
    );

    unlockScroll(panel);

    if (panel.open) {
      panel.classList.remove(
        'theme-drawer__dialog--opening',
        'theme-drawer__dialog--opening-inline-start'
      );

      panel.classList.add(
        'theme-drawer__dialog--closing'
      );

      await onAnimationEnd(
        panel,
        undefined,
        {
          subtree: false,
        }
      );

      panel.classList.remove(
        'theme-drawer__dialog--closing'
      );
    }

    panel.close();

    this.style.removeProperty(
      '--drawer-stack-order'
    );

    if (
      !document.querySelector(
        'theme-drawer[open]'
      )
    ) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger =
      this.#previouslyFocused;

    this.#previouslyFocused = null;

    if (
      trigger &&
      document.contains(trigger)
    ) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (
        document.querySelector(
          `[aria-controls="${this.id}"]`
        )
      )?.focus();
    }

    this.#isClosing = false;

    if (this.#deferredOpen) {
      const options =
        this.#deferredOpen;

      this.#deferredOpen = false;

      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    if (this.#isClosing) {
      this.#deferredOpen = false;
      return;
    }

    await this.#closeDialog();
  }

  /**
   * Toggles drawer.
   */
  toggle() {
    if (
      this.isOpen &&
      this.#isTopmost()
    ) {
      return this.close();
    }

    return this.open();
  }

  /**
   * @returns {boolean}
   */
  #isTopmost() {
    const myOrder = Number(
      this.style.getPropertyValue(
        '--drawer-stack-order'
      ) || 0
    );

    for (
      const other of document.querySelectorAll(
        'theme-drawer[open]'
      )
    ) {
      if (other === this) continue;

      const otherOrder = Number(
        /** @type {HTMLElement} */ (
          other
        ).style.getPropertyValue(
          '--drawer-stack-order'
        ) || 0
      );

      if (otherOrder > myOrder) {
        return false;
      }
    }

    return true;
  }
}

if (
  !customElements.get(
    'theme-drawer'
  )
) {
  customElements.define(
    'theme-drawer',
    ThemeDrawer
  );
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(
      DrawerOpenEvent.eventName,
      {
        bubbles: true,
      }
    );
  }

  static eventName =
    'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(
      DrawerCloseEvent.eventName,
      {
        bubbles: true,
      }
    );
  }

  static eventName =
    'theme-drawer:close';
}

document.addEventListener(
  'DOMContentLoaded',
  function () {
    const menuLinks =
      document.querySelectorAll(
        '.menu-drawer__menu-item'
      );

    menuLinks.forEach(
      function (link) {
        link.addEventListener(
          'click',
          function () {
            const closeButton =
              document.querySelector(
                '.menu-drawer__close-button'
              );

            if (closeButton) {
              setTimeout(
                function () {
                  closeButton.click();
                },
                100
              );
            }
          }
        );
      }
    );
  }
);